import React, { useState, useEffect } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { settingAPI } from '../../actions/imsServer';
import { LOCALE } from '../../constants';
import ContentArea from '../contentArea';
import Button from '../button';
import RadioBt, { RadioGroup } from '../radioBt';

export const Language = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [tempSelectedLanguage, setTempSelectedLanguage] = useState(i18n.language);
    const [settingExists, setSettingExists] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const hospitalId = JSON.parse(localStorage.getItem('meInfo'))?.hospitalId;
    const type = useLocation().state?.type || 'auth';

    useEffect(() => {
        const fetchSetting = async () => {
            try {
                if (localStorage.getItem('meInfo')) {
                    const settingResponse = await settingAPI.getSettings({
                        userId: JSON.parse(localStorage.getItem('meInfo'))?.id,
                        name: 'language',
                    });
                    const settings = settingResponse.data?.settings || [];

                    if (settings.length > 0) {
                        setSettingExists(true);
                        await i18n.changeLanguage(settings[0].value);
                        localStorage.setItem('language', settings[0].value);
                    }
                }
            } catch (error) {
                console.error('Error fetching language setting:', error);
            }
        };
        fetchSetting();
    }, [hospitalId, i18n]);

    const handleLanguageClick = async code => {
        setTempSelectedLanguage(code);
    };

    const handleChangeLanguage = async () => {
        try {
            await i18n.changeLanguage(tempSelectedLanguage);
            localStorage.setItem('language', tempSelectedLanguage);

            if (localStorage.getItem('meInfo')) {
                if (settingExists) {
                    await settingAPI.updateSetting({
                        name: 'language',
                        value: tempSelectedLanguage,
                    });
                    setSettingExists(true);
                } else {
                    await settingAPI.registerSetting({
                        name: 'language',
                        value: tempSelectedLanguage,
                    });
                    setSettingExists(true);
                }
            }

            setSnackbarMessage(t('success'));
            setSnackbarOpen(true);

            setTimeout(() => {
                navigate(-1);
            }, 1000);
        } catch (error) {
            console.error('Error changing language:', error);
            setSnackbarMessage(t('medication_setting_save_error'));
            setSnackbarOpen(true);
        }
    };

    return (
        <ContentArea
            sx={{
                padding: '0 10px 80px 10px',
                width: '100%',
                maxWidth: '720px',
                margin: '0 auto',
            }}
        >
            {/* Language options list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                <RadioGroup
                    value={tempSelectedLanguage}
                    onChange={(e) => handleLanguageClick(e.target.value)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                        width: '100%',
                    }}
                >
                    {LOCALE.map((lang) => (
                        <RadioBt
                            key={lang.code}
                            value={lang.code}
                            label={`${i18n.getFixedT(lang.code)(lang.name)} (${t(lang.name)})`}
                        />
                    ))}
                </RadioGroup>
            </Box>

            {/* Save button */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '32px',
                    width: '100%',
                }}
            >
                <Button onClick={handleChangeLanguage}>
                    {t('save')}
                </Button>
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: { xs: '60px', sm: '20px' } }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    sx={{
                        width: '100%',
                        fontSize: '14px',
                        fontWeight: 500,
                        boxShadow: theme.palette.shadows?.custom?.tooltip || '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </ContentArea>
    );
};
